<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import axios from 'axios'
import MarkdownIt from 'markdown-it'
import { ElMessage } from 'element-plus'
import kefuQr from '@/assets/img/company/kefuWechat.jpg'

const props = defineProps<{ 
  modelValue: boolean
  fetchUrl?: string // 获取活动描述的接口地址，返回 markdown 字符串或 { content: string }
  title?: string
  wechatId?: string
  qrSrc?: string
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
}>()

const dialogVisible = computed({
  get: () => props.modelValue,
  set: (val: boolean) => emit('update:modelValue', val)
})

const md = new MarkdownIt({ html: true, linkify: true, breaks: true })
const loading = ref(false)
const loadError = ref('')
const renderedHtml = ref('')

const resolvedWechatId = computed(() => props.wechatId ?? 'yinghuohaihai')
const resolvedQrSrc = computed(() => props.qrSrc ?? kefuQr)

async function fetchMarkdown() {
  if (!props.fetchUrl) return
  loading.value = true
  loadError.value = ''
  try {
    const res = await axios.get(props.fetchUrl)
    const data = res?.data
    let mdText = ''
    if (typeof data === 'string') {
      mdText = data
    } else if (data && typeof data.content === 'string') {
      mdText = data.content
    } else if (data && typeof data.markdown === 'string') {
      mdText = data.markdown
    } else if (data && typeof data.data === 'string') {
      mdText = data.data
    }
    renderedHtml.value = md.render(mdText || '')
  } catch (e: any) {
    loadError.value = '活动内容获取失败，请稍后重试'
  } finally {
    loading.value = false
  }
}

watch(
  () => dialogVisible.value,
  (val) => {
    if (val) {
      // 打开弹窗时再去拉取，避免未使用时请求
      fetchMarkdown()
    }
  },
  { immediate: false }
)

async function copyWechatId() {
  try {
    await navigator.clipboard.writeText(resolvedWechatId.value)
    ElMessage.success('复制成功')
  } catch (err) {
    ElMessage.error('复制失败，请手动复制')
  }
}
</script>

<template>
  <el-dialog
    v-model="dialogVisible"
    :title="title || '活动详情'"
    width="340px"
    class="activity-dialog"
    center
  >
    <div class="activity-content">
      <template v-if="loading">
        <el-skeleton :rows="6" animated />
      </template>
      <template v-else-if="loadError">
        <el-alert :title="loadError" type="error" show-icon />
      </template>
      <template v-else>
        <div class="markdown-content" v-html="renderedHtml"></div>
      </template>

      <div class="customer-service-info">
        <span>客服微信号：{{ resolvedWechatId }}</span>
        <el-button size="small" class="copy-btn" @click="copyWechatId">复制</el-button>
      </div>

      <div class="qr-section">
        <img class="qr-image" :src="resolvedQrSrc" alt="客服微信二维码" />
        <div class="qr-tip">扫码加微信</div>
      </div>
    </div>
  </el-dialog>
</template>

<style lang="scss">
.activity-dialog {
  .el-dialog__body {
    padding: 12px 12px 8px 12px;
    background: #fafbfc;
  }
}

.activity-content {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.markdown-content {
  background: #fff;
  border-radius: 8px;
  padding: 12px;
  color: #333;
  line-height: 1.6;
  font-size: 14px;
  border: 1px solid #eee;
  // markdown 常见元素微样式
  h1, h2, h3, h4, h5, h6 { margin: 0.4em 0; }
  p { margin: 0.4em 0; }
  ul, ol { padding-left: 1.2em; }
  code { background: #f6f8fa; padding: 2px 4px; border-radius: 4px; }
  pre { background: #f6f8fa; padding: 8px; border-radius: 6px; overflow: auto; }
  a { color: #357abd; text-decoration: underline; }
}

.customer-service-info {
  text-align: center;
  color: #888;
  font-size: 13px !important;
  padding: 8px 12px;
  margin-top: 0px;
  background: #fafbfc;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.copy-btn {
  background: linear-gradient(135deg, #4a90e2, #357abd) !important;
  color: #fff !important;
  padding: 4px 8px !important;
  border-radius: 12px !important;
  font-size: 12px !important;
  font-weight: bold !important;
  text-shadow: 0 1px 2px rgba(0,0,0,0.3);
  box-shadow: 0 2px 6px rgba(74, 144, 226, 0.3);
  border: none !important;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(74, 144, 226, 0.4);
  }
}

.qr-section {
  background: #fff;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px solid #eee;
}

.qr-image {
  width: 180px;
  height: 180px;
  object-fit: cover;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.qr-tip {
  margin-top: 8px;
  color: #666;
  font-size: 12px;
}
</style>
